import { ConfirmationModal } from "@/components/molecules/ConfirmationModal/ConfirmationModal";
import { Button, Calendar, Download, Filter, Input, Plus, Search, useDebounce } from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppointmentCard } from "../../components/molecules/AppointmentCard/AppointmentCard";
import { AppointmentFilters } from "../../components/molecules/AppointmentFilters/AppointmentFilters";
import { AppointmentForm } from "../../components/molecules/AppointmentForm";
import { LicenceGate } from "../../components/atoms/LicenceGate";
import { LicenceModal } from "../../components/molecules/LicenceModal/LicenceModal";
import { PageHeader } from "../../components/molecules/PageHeader/PageHeader";
import { Pagination } from "../../components/molecules/Pagination/Pagination";
import useFacilityUser from "../../hooks/useFacilityUser";
import { useHasLicence } from "../../hooks/useHasLicence";
import { appointmentApi } from "../../services/appointment.api";
import {
  addAppointmentToList,
  fetchAppointmentsRequest,
  resetFilters,
  setAppointmentToCancel,
  setCurrentPage,
  setFilterStatus,
  setFilterType,
  setIsCreating,
  setIsUpdating,
  setItemsPerPage,
  setSearchTerm,
  setSelectedDate,
  setShowAddModal,
  setShowCancelModal,
  setShowFilters,
  updateAppointmentInList,
} from "../../store/reducers/appointmentSlice";
import { fetchStatsRequest } from "../../store/reducers/dashboardSlice";
import { RootState } from "../../store/store";
import {
  Appointment,
  AppointmentStatusEnum,
} from "../../types/appointment.definition";
import { Order } from "../../types/search.definition";
import { exportAppointmentsToCSV } from "../../utils/export.utils";
import { fetchAllFacilityAppointments } from "../../services/appointment-export.api";

export const ManageAppointments: React.FC = () => {
  const { t } = useTranslation();
  const facilityUser = useFacilityUser();
  const hasLicence = useHasLicence();
  const [showLicenceModal, setShowLicenceModal] = useState(false);
  const dispatch = useDispatch();
  const facilityId = Number(facilityUser.healthcareFacility?.id);

  // Get state from Redux store
  const {
    appointments,
    loading: isLoading,
    error,
    searchTerm,
    filterStatus,
    filterType,
    selectedDate,
    currentPage,
    itemsPerPage,
    totalItems,
    showFilters,
    showAddModal,
    showCancelModal,
    appointmentToCancel,
    isUpdating,
    isCreating,
  } = useSelector((state: RootState) => state.appointments);

  // Local state for UI messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Debounce search term to improve performance
  const debouncedSearchTerm = useDebounce(searchTerm);

  // Use ref to track last request and prevent duplicate calls
  const lastRequestRef = useRef<string>("");

  // Fetch appointments when filters, pagination, or facility changes
  useEffect(() => {
    if (!facilityId) return;

    // Create search request with current filters
    const request = {
      sortBy: {
        property: "appointmentTime",
        order: Order.DESC,
      },
      query: {
        keyword: debouncedSearchTerm || "",
        status: filterStatus || undefined,
        appointmentType: filterType || undefined,
        date: selectedDate || undefined,
      },
      paginationSettings: {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      },
    };

    // Create a unique key for this request to prevent duplicate calls
    const requestKey = JSON.stringify({
      facilityId,
      keyword: debouncedSearchTerm,
      status: filterStatus,
      appointmentType: filterType,
      date: selectedDate,
      page: currentPage,
      limit: itemsPerPage,
    });

    // Only dispatch if this is a different request
    if (lastRequestRef.current !== requestKey && !isLoading) {
      lastRequestRef.current = requestKey;
      dispatch(
        fetchAppointmentsRequest({
          facilityId,
          searchRequest: request,
        })
      );
    }
  }, [
    debouncedSearchTerm,
    filterStatus,
    filterType,
    selectedDate,
    currentPage,
    itemsPerPage,
    facilityId,
    dispatch,
    isLoading,
  ]);

  // Handle page change
  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    dispatch(setItemsPerPage(newItemsPerPage));
  };

  // Handle appointment status change
  const handleStatusChange = async (
    appointmentId: string,
    newStatus: string
  ) => {
    // If canceling, show confirmation modal
    if (newStatus === "CANCELED") {
      const appointment = appointments.find((apt) => apt.id === appointmentId);
      if (appointment) {
        dispatch(setAppointmentToCancel(appointment));
        dispatch(setShowCancelModal(true));
        return;
      }
    }

    await updateAppointmentStatus(appointmentId, newStatus);
  };

  // Handle appointment status update (after confirmation)
  const updateAppointmentStatus = async (
    appointmentId: string,
    newStatus: string
  ) => {
    dispatch(setIsUpdating(appointmentId));

    try {
      const appointmentToUpdate = appointments.find(
        (apt) => apt.id === appointmentId
      );

      if (!appointmentToUpdate) {
        throw new Error("Appointment not found");
      }

      const updatedAppointment = {
        ...appointmentToUpdate,
        status: newStatus,
      };

      await appointmentApi.updateAppointment(appointmentId, updatedAppointment);

      // Update Redux state
      dispatch(
        updateAppointmentInList({
          id: appointmentId,
          updates: { status: newStatus },
        })
      );

      // Show success message
      setSuccessMessage(t("appointment.manage.statusUpdated"));

      // Refresh facility statistics after appointment update
      if (facilityUser.healthcareFacility?.id) {
        dispatch(
          fetchStatsRequest({ facilityId: facilityUser.healthcareFacility.id })
        );
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      console.error("Error updating appointment status:", err);
      setSuccessMessage(null);
    } finally {
      dispatch(setIsUpdating(null));
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirm = async () => {
    if (appointmentToCancel) {
      await updateAppointmentStatus(appointmentToCancel.id!, "CANCELED");
      dispatch(setShowCancelModal(false));
      dispatch(setAppointmentToCancel(null));
    }
  };

  // Handle cancel modal close
  const handleCancelModalClose = () => {
    dispatch(setShowCancelModal(false));
    dispatch(setAppointmentToCancel(null));
  };

  // Handle export appointments
  // MED-1687: export every appointment matching the filters, not only the page on screen
  const [isExporting, setIsExporting] = useState(false);
  const handleExportAppointments = async () => {
    if (appointments.length === 0 || !facilityId) {
      setSuccessMessage(null);
      return;
    }

    setIsExporting(true);
    try {
      const allAppointments = await fetchAllFacilityAppointments(facilityId, {
        keyword: debouncedSearchTerm || "",
        status: filterStatus || undefined,
        appointmentType: filterType || undefined,
        date: selectedDate || undefined,
      });
      exportAppointmentsToCSV(allAppointments, t);
      setSuccessMessage(t("appointment.manage.exported"));
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error exporting appointments:", err);
      setSuccessMessage(null);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle appointment creation
  const handleCreateAppointment = async (
    appointmentData: Partial<Appointment>
  ) => {
    dispatch(setIsCreating(true));
    setSuccessMessage(null);

    try {
      // Validate required fields
      if (!appointmentData.patientId) {
        throw new Error(t("appointment.manage.errors.patientRequired"));
      }
      if (!appointmentData.doctorId) {
        throw new Error(t("appointment.manage.errors.doctorRequired"));
      }
      if (!appointmentData.appointmentTime) {
        throw new Error(t("appointment.manage.errors.timeRequired"));
      }
      if (!appointmentData.reason) {
        throw new Error(t("appointment.manage.errors.reasonRequired"));
      }

      // Prepare the appointment data for the API
      const appointmentPayload: Partial<Appointment> = {
        ...appointmentData,
        healthcareFacilityId: facilityId,
        status: AppointmentStatusEnum.CONFIRMED,
      };

      // Create the appointment using the API
      const response = await appointmentApi.createAppointment(
        appointmentPayload as Appointment
      );

      // Add to Redux store
      if (response) {
        dispatch(addAppointmentToList(response));
      }

      // Close the modal
      dispatch(setShowAddModal(false));

      // Show success message
      setSuccessMessage(t("appointment.manage.created"));

      // Refresh the appointment list
      const request = {
        sortBy: {
          property: "appointmentTime",
          order: Order.DESC,
        },
        query: {
          keyword: debouncedSearchTerm || "",
          status: filterStatus || undefined,
          appointmentType: filterType || undefined,
          date: selectedDate || undefined,
        },
        paginationSettings: {
          limit: itemsPerPage,
          offset: (currentPage - 1) * itemsPerPage,
        },
      };
      // Reset the last request ref to allow refresh
      lastRequestRef.current = "";
      dispatch(
        fetchAppointmentsRequest({
          facilityId,
          searchRequest: request,
        })
      );

      // Refresh facility statistics after appointment creation
      if (facilityUser.healthcareFacility?.id) {
        dispatch(
          fetchStatsRequest({ facilityId: facilityUser.healthcareFacility.id })
        );
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err: unknown) {
      console.error("Error creating appointment:", err);
      setSuccessMessage(null);
    } finally {
      dispatch(setIsCreating(false));
    }
  };
  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <PageHeader
        title={t("appointment.manage.title")}
        description={t("appointment.manage.subtitle")}
        icon={Calendar}
        actions={
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExportAppointments}
              disabled={appointments.length === 0 || isLoading || isExporting}
            >
              <Download className="mr-2 w-4 h-4" />
              {t("appointment.manage.export")}
            </Button>
            <LicenceGate
              isLicenced={hasLicence}
              tooltipText={t("licence.requiredTooltip")}
              onRestrictedClick={() => setShowLicenceModal(true)}
            >
              <Button
                size="sm"
                onClick={() => dispatch(setShowAddModal(true))}
              >
                <Plus className="mr-2 w-4 h-4" />
                {t("appointment.manage.newAppointment")}
              </Button>
            </LicenceGate>
          </div>
        }
      />

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="p-4 mb-4 text-sm text-green-800 bg-green-50 rounded-lg border border-green-200">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="p-4 mb-4 text-sm text-red-800 bg-red-50 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="p-6 card-base">
        <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:space-x-4 lg:space-y-0">
          <div className="flex-1">
            <div className="relative">
              <Input
                leftIcon={<Search />}
                placeholder={t("appointment.manage.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => dispatch(setSearchTerm(e.target.value))}
              />
              {searchTerm !== debouncedSearchTerm &&
                searchTerm.trim().length > 0 && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 rounded-full animate-pulse bg-primary-500"></div>
                  </div>
                )}
            </div>
            {searchTerm !== debouncedSearchTerm &&
              searchTerm.trim().length > 0 && (
                <p className="mt-1 text-xs text-neutral-500">
                  {t("appointment.manage.searching")}
                </p>
              )}
          </div>
          <div className="flex gap-2 sm:gap-4">
            <Input
              type="date"
              value={
                selectedDate
                  ? new Date(selectedDate).toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) =>
                dispatch(
                  setSelectedDate(
                    e.target.value ? new Date(e.target.value) : null
                  )
                )
              }
              leftIcon={<Calendar />}
            />
            <Button
              variant={showFilters ? "ews-primary" : "ghost"}
              onClick={() => dispatch(setShowFilters(!showFilters))}
            >
              <Filter className="mr-2 w-4 h-4" />
              {t("appointment.manage.filters")}
              {(filterStatus || filterType) && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-ews-primary-100 text-ews-primary-800 rounded-full">
                  {[filterStatus, filterType].filter(Boolean).length}
                </span>
              )}
            </Button>
          </div>
        </div>

        {showFilters && (
          <AppointmentFilters
            filterStatus={filterStatus}
            setFilterStatus={(status) => dispatch(setFilterStatus(status))}
            filterType={filterType}
            setFilterType={(type) => dispatch(setFilterType(type))}
            hasActiveFilters={!!(filterStatus || filterType)}
            onClearFilters={() => dispatch(resetFilters())}
          />
        )}
      </div>

      {/* Appointments List */}
      <div className="space-y-4">
        {appointments.map((appointment) => (
          <AppointmentCard
            key={appointment.id}
            appointment={appointment}
            isUpdating={isUpdating === appointment.id}
            onStatusChange={handleStatusChange}
          />
        ))}

        {appointments.length === 0 && !isLoading && (
          <div className="py-12 text-center card-base">
            <Calendar className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("appointment.manage.noAppointments")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("appointment.manage.adjustFilters")}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          isLoading={isLoading}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelModal}
        title={t("appointment.manage.cancelTitle")}
        message={t("appointment.manage.cancelConfirmation", {
          name: `${appointmentToCancel?.patient?.firstName} ${appointmentToCancel?.patient?.lastName}`,
        })}
        confirmLabel={t("appointment.manage.confirmCancel")}
        cancelLabel={t("appointment.manage.keepAppointment")}
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelModalClose}
        variant="danger"
        isLoading={isUpdating === appointmentToCancel?.id}
      />

      {showAddModal && (
        <AppointmentForm
          onSubmit={handleCreateAppointment}
          onCancel={() => dispatch(setShowAddModal(false))}
          isSubmitting={isCreating}
          facilityId={facilityId}
        />
      )}

      <LicenceModal
        isOpen={showLicenceModal}
        onClose={() => setShowLicenceModal(false)}
      />
    </div>
  );
};
